import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  LockKeyhole,
  MapPin,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Wrench,
  ZoomIn,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { supabase } from '../utils/supabase';
import { Product, ProductImage } from '../types';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { ProductDescription } from '../components/ProductDescription';

function getStockLevel(product: Product): number {
  const databaseProduct = product as Product & { stock_level?: number };
  return Number(databaseProduct.stock_level ?? product.stockLevel ?? 0);
}

function getImageUrl(image: ProductImage | string | undefined): string {
  if (!image) return '';
  return typeof image === 'string' ? image : image.url;
}

function normalizeProduct(data: unknown): Product {
  const raw = data as Product & {
    stock_level?: number;
    images?: ProductImage[];
    features?: string[];
  };

  return {
    ...raw,
    price: Number(raw.price ?? 0),
    stockLevel: Number(raw.stockLevel ?? raw.stock_level ?? 0),
    images: Array.isArray(raw.images) ? raw.images : [],
    features: Array.isArray(raw.features) ? raw.features : [],
  };
}

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  React.useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;
        if (!data) {
          setError('Product not found');
          return;
        }

        setProduct(normalizeProduct(data));
        setSelectedImageIndex(0);
      } catch (fetchError) {
        console.error('Error fetching product:', fetchError);
        setError('Product not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();

    const subscription = supabase
      .channel(`product_changes_${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
          filter: `id=eq.${id}`,
        },
        () => {
          fetchProduct();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product);
    navigate('/cart');
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="px-4 text-center">
          <AlertCircle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-semibold text-slate-900">Product Not Found</h2>
          <p className="mb-6 text-slate-600">The product you’re looking for doesn’t exist.</p>
          <Link
            to="/shop"
            className="inline-flex items-center font-semibold text-blue-700 hover:text-blue-900"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images || [];
  const stockLevel = getStockLevel(product);
  const imageCount = Math.max(images.length, 1);
  const selectedImage = getImageUrl(images[selectedImageIndex]);
  const condition = product.category || 'Used';

  const showPreviousImage = () => {
    if (images.length < 2) return;
    setSelectedImageIndex((current) => (current - 1 + images.length) % images.length);
  };

  const showNextImage = () => {
    if (images.length < 2) return;
    setSelectedImageIndex((current) => (current + 1) % images.length);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <nav className="mb-6 overflow-x-auto" aria-label="Breadcrumb">
          <ol className="flex min-w-max items-center gap-2 text-sm">
            <li>
              <Link to="/" className="font-medium text-blue-700 hover:text-blue-900">
                Home
              </Link>
            </li>
            <li className="text-slate-400">/</li>
            <li>
              <Link to="/shop" className="font-medium text-blue-700 hover:text-blue-900">
                Shop
              </Link>
            </li>
            <li className="text-slate-400">/</li>
            <li className="max-w-[240px] truncate text-slate-600 sm:max-w-lg">{product.name}</li>
          </ol>
        </nav>

        <div className="grid items-start gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
          <div className="space-y-4">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.12)]">
              <div className="flex aspect-[4/5] items-center justify-center bg-slate-100 sm:aspect-[5/4] lg:aspect-[4/5]">
                <ImageWithFallback
                  src={selectedImage}
                  alt={`${product.name} - View ${selectedImageIndex + 1}`}
                  className="h-full w-full object-contain"
                  fallbackText={product.name}
                />
              </div>

              <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-3.5 py-2 text-xs font-black uppercase tracking-wide text-white shadow-lg">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                90-Day Warranty
              </div>

              <div className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-md">
                <ZoomIn className="h-5 w-5" aria-hidden="true" />
              </div>

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={showPreviousImage}
                    className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-lg transition hover:bg-white"
                    aria-label="Previous product image"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={showNextImage}
                    className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-lg transition hover:bg-white"
                    aria-label="Next product image"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              <div className="absolute bottom-4 right-4 z-10 rounded-full bg-slate-950/85 px-3 py-1.5 text-xs font-bold text-white">
                {selectedImageIndex + 1} / {imageCount}
              </div>
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-3 sm:grid-cols-6">
                {images.map((image, index) => (
                  <button
                    type="button"
                    key={image.id || index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square overflow-hidden rounded-2xl border-2 bg-white p-1 shadow-sm transition ${
                      selectedImageIndex === index
                        ? 'border-blue-700 ring-2 ring-blue-100'
                        : 'border-slate-200 hover:border-blue-400'
                    }`}
                    aria-label={`View product image ${index + 1}`}
                  >
                    <ImageWithFallback
                      src={getImageUrl(image)}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className="h-full w-full rounded-xl object-cover"
                      fallbackText="View"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:p-8">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-blue-700">
                  {condition} Appliance
                </span>
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
                    stockLevel > 0
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${stockLevel > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                  />
                  {stockLevel > 0 ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>

              <h1 className="text-3xl font-black leading-tight tracking-tight text-slate-950 sm:text-4xl">
                {product.name}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
                <span>{product.brand}</span>
                {product.type && (
                  <>
                    <span aria-hidden="true">•</span>
                    <span>{product.type}</span>
                  </>
                )}
              </div>

              <p className="mt-6 text-4xl font-black tracking-tight text-blue-700 sm:text-5xl">
                ${Number(product.price).toFixed(2)}
              </p>

              <div className="my-6 h-px bg-slate-200" />

              <ProductDescription
                description={product.description || 'Contact us for product details.'}
                className="relative text-base leading-7 text-slate-700"
              />

              {product.features.length > 0 && (
                <div className="mt-6 grid gap-2 sm:grid-cols-2">
                  {product.features.slice(0, 6).map((feature, index) => (
                    <div key={`${feature}-${index}`} className="flex items-start gap-2 text-sm text-slate-700">
                      <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" aria-hidden="true" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-7 grid overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 sm:grid-cols-3">
                <div className="flex items-start gap-3 px-4 py-5">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <Wrench className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-sm font-extrabold text-slate-900">Installation</h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Service available</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 border-t border-slate-200 px-4 py-5 sm:border-l sm:border-t-0">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-sm font-extrabold text-slate-900">90-Day Warranty</h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Coverage included</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 border-t border-slate-200 px-4 py-5 sm:border-l sm:border-t-0">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <Truck className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-sm font-extrabold text-slate-900">Pickup & Delivery</h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Local options available</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:p-6">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={stockLevel === 0}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-blue-700 px-6 py-4 text-base font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-400 sm:text-lg"
              >
                <ShoppingCart className="h-5 w-5" aria-hidden="true" />
                {stockLevel === 0 ? 'Out of Stock' : 'Add to Cart'}
              </button>

              <Link
                to="/booking"
                state={{ product }}
                className="mt-3 flex w-full items-center justify-center gap-3 rounded-2xl bg-green-600 px-6 py-4 text-base font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-green-500 sm:text-lg"
              >
                <CalendarDays className="h-5 w-5" aria-hidden="true" />
                Book Installation
              </Link>

              <div className="mt-5 grid gap-4 border-t border-slate-200 pt-5 sm:grid-cols-3">
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-extrabold text-slate-900">Local Pickup</p>
                    <p className="mt-1 text-xs text-slate-500">Available at our store</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Truck className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-extrabold text-slate-900">Delivery</p>
                    <p className="mt-1 text-xs text-slate-500">Fees may apply</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Wrench className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-extrabold text-slate-900">Installation</p>
                    <p className="mt-1 text-xs text-slate-500">Available with delivery</p>
                  </div>
                </div>
              </div>

              <p className="mt-5 flex items-center justify-center gap-2 text-center text-xs font-medium text-slate-500">
                <LockKeyhole className="h-4 w-4" aria-hidden="true" />
                Payment due upon successful installation
              </p>
            </div>
          </div>
        </div>

        <section className="mt-10 overflow-hidden rounded-3xl border border-blue-200 bg-blue-50 shadow-sm">
          <div className="grid gap-5 px-6 py-7 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-7 w-7 flex-shrink-0 text-blue-700" aria-hidden="true" />
              <div>
                <h2 className="font-black text-blue-950">90-Day Warranty</h2>
                <p className="mt-1 text-sm text-blue-900/65">Coverage included with purchase</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BadgeCheck className="mt-0.5 h-7 w-7 flex-shrink-0 text-blue-700" aria-hidden="true" />
              <div>
                <h2 className="font-black text-blue-950">Inspected & Tested</h2>
                <p className="mt-1 text-sm text-blue-900/65">Checked before sale</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Truck className="mt-0.5 h-7 w-7 flex-shrink-0 text-blue-700" aria-hidden="true" />
              <div>
                <h2 className="font-black text-blue-950">Local Service</h2>
                <p className="mt-1 text-sm text-blue-900/65">Pickup and delivery options</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wrench className="mt-0.5 h-7 w-7 flex-shrink-0 text-blue-700" aria-hidden="true" />
              <div>
                <h2 className="font-black text-blue-950">Installation Available</h2>
                <p className="mt-1 text-sm text-blue-900/65">Ask about setup service</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
